import React, { FC, useState, useRef } from 'react';
import translate from '@translate';
import { checkFormValidity } from 'src/utils/formFunctions';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { EditKeysType } from './ProductsTable';
import { ProductCompletionSheetFinalProductType } from 'src/types/productCompletionSheet';
import Select from 'src/components/Select';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { BillOfMaterialsProductType, BillOfMaterialsType } from 'src/types/billOfMaterials';
import { FormErrorsType } from '../../types/global';

type NewProductBlockPropsType = {
    data?: ProductCompletionSheetFinalProductType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    billsOfMaterialNameConstatns?: ConstantsCodeType[];
    editErrors: FormErrorsType;
};

const initData: ProductCompletionSheetFinalProductType = { LINE_STATE: 'E' };

const NewProductBlock: FC<NewProductBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<ProductCompletionSheetFinalProductType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        if (key === 'ID_BILL_OF_MATERIALS') {
            newData.ID_BILL_OF_MATERIALS = Number(value);
            const billOfMaterial = props.billsOfMaterialNameConstatns?.find((item) => item.value === Number(value));
            newData.ID_FINAL_PRODUCT = billOfMaterial?.oreginalData?.ID_FINAL_PRODUCT;
            newData.PRODUCT_COMPLETION_FINAL_PRODUCT_PRODUCTS = billOfMaterial?.oreginalData?.PRODUCTS?.filter(
                (product: BillOfMaterialsProductType) => product.LINE_STATE === 'E'
            );
        } else {
            newData[key] = value;
        }
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId = Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ID)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ID: newId,
                },
            ]);

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography
                gutterBottom
                variant="h3"
                sx={props.editErrors.PRODUCT_COMPLETION_FINAL_PRODUCTS ? { color: colors.alert_O } : {}}
            >
                {translate('productCompletionSheetDetail.action.addBillOfMaterials')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={6}>
                        <Select
                            options={props.billsOfMaterialNameConstatns || []}
                            value={editData.ID_BILL_OF_MATERIALS}
                            error={editErrors.ID_BILL_OF_MATERIALS}
                            name="ID_BILL_OF_MATERIALS"
                            label={translate('productCompletionSheetDetail.finalProductsTable.billOfMaterials')}
                            fullWidth
                            required
                            onChange={(value, error) => {
                                if (!Array.isArray(value)) {
                                    changeValue('ID_BILL_OF_MATERIALS', value, error);
                                }
                            }}
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.COUNT}
                            error={editErrors.COUNT}
                            onChange={(value, error) => changeValue('COUNT', value, error)}
                            name="COUNT"
                            label={translate('productCompletionSheetDetail.finalProductsTable.count')}
                            fullWidth
                            required
                            type="numberLocale"
                            min={1}
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.LOT}
                            error={editErrors.LOT}
                            onChange={(value, error) => changeValue('LOT', value, error)}
                            name="LOT"
                            label={translate('productCompletionSheetDetail.finalProductsTable.lot')}
                            fullWidth
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD }}
                            mobileFullWidht
                        >
                            + {translate('productCompletionSheetDetail.finalProductsTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewProductBlock;
